// Живые подсказки по формулам (registry.lint): громоздкая формула в строке (4.6.1.1), формула вплотную к слову,
// формула не приведена к СТО, строка расчёта с номером (4.6.1.8), экспликация (4.6.1.7): не пояснённые
// символы, символы, которых нет в формуле; двоеточие перед формулой (4.6.1.3), пустая формула, команды,
// которые экспорт не знает. Каждая подсказка - с быстрым исправлением.
import type { RegistryApi, LintIssue, Ctx } from '../../core/registry';
import type { DocIndex } from '../../core/docIndex';
import { canon, isBulky, isCalcLine, symbols, symKey, unknownCommands, linearizeFractions } from './latex';
import { replaceFormula } from './views';

const CYR = /[А-Яа-яЁё]/;

/** Найти позицию узла по проверке (позиции меняются - ищем при исправлении). */
function findPos(ctx: Ctx, test: (n: any, pos: number) => boolean): number {
  let at = -1;
  ctx.editor?.state.doc.descendants((n, pos) => {
    if (at >= 0) return false;
    if (test(n, pos)) { at = pos; return false; }
    return true;
  });
  return at;
}

function setAttrs(ctx: Ctx, id: string, patch: Record<string, unknown>) {
  const ed = ctx.editor;
  if (!ed) return;
  const pos = findPos(ctx, (n) => n.attrs?.id === id);
  if (pos < 0) return;
  const n = ed.state.doc.nodeAt(pos)!;
  ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, ...patch }));
}

export function setupLint(r: RegistryApi) {
  r.lint.add({ id: 'math.rules', title: 'Формулы', order: 40, run: (ix: DocIndex) => {
    const out: LintIssue[] = [];
    const explained = new Map<string, boolean>();
    // --- строчные формулы (по абзацам)
    ix.doc.descendants((n, _pos, parent, index) => {
      if (n.type.name !== 'mathInline') return true;
      const pid = findParentId(ix, _pos);
      const latex = n.attrs.latex || '';
      if (isBulky(latex)) {
        out.push({ sev: 'E', rule: '4.6.1.1', node: pid, msg: 'Громоздкая формула (дробь, сумма, интеграл, корень, предел) в строке текста - вынесите её в отдельную строку',
          fix: { label: 'Вынести в отдельную строку', run: (c) => {
            const at = findPos(c, (m) => m.type.name === 'mathInline' && m.attrs.latex === latex);
            if (at >= 0) replaceFormula(c.editor, at, 'mathInline', { latex, numbered: true, where: [], display: true });
          } } });
        if (/\\frac/.test(latex) && !/\\(sum|int|sqrt|lim|begin|prod)/.test(latex)) {
          out.push({ sev: 'I', rule: '4.6.1.1', node: pid, msg: 'Дробь в строке можно записать через косую черту: ' + linearizeFractions(latex),
            fix: { label: 'Дробь через «/»', run: (c) => {
              const at = findPos(c, (m) => m.type.name === 'mathInline' && m.attrs.latex === latex);
              if (at >= 0) c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(at, undefined, { latex: linearizeFractions(latex) }));
            } } });
        }
      }
      const cn = canon(latex);
      if (latex && cn.latex !== latex && cn.notes.length) {
        out.push({ sev: 'W', node: pid, msg: 'Формула в строке не по СТО (кнопка «Исправить» приведёт её к СТО): ' + cn.notes.map((x) => x.msg).join('; '),
          fix: { label: 'Исправить', run: (c) => {
            const at = findPos(c, (m) => m.type.name === 'mathInline' && m.attrs.latex === latex);
            if (at >= 0) c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(at, undefined, { latex: cn.latex }));
          } } });
      }
      // вплотную к слову (FORMULA-SPACE)
      const idx = parent ? index : -1;
      if (parent && idx >= 0) {
        const prev = idx > 0 ? parent.child(idx - 1) : null;
        const next = idx < parent.childCount - 1 ? parent.child(idx + 1) : null;
        const glueL = prev && prev.isText && CYR.test((prev.text || '').slice(-1));
        const glueR = next && next.isText && CYR.test((next.text || '').slice(0, 1));
        if (glueL || glueR) {
          out.push({ sev: 'W', rule: '4.6.1.3', node: pid, msg: 'Формула в строке вплотную к слову - нужен пробел',
            fix: { label: 'Вставить пробел', run: (c) => {
              const at = findPos(c, (m) => m.type.name === 'mathInline' && m.attrs.latex === latex);
              if (at < 0) return;
              const ed = c.editor!;
              let tr = ed.state.tr;
              if (glueR) tr = tr.insertText(' ', at + 1);
              if (glueL) tr = tr.insertText(' ', at);
              ed.view.dispatch(tr);
            } } });
        }
      }
      return false;
    });
    // --- выключные формулы
    const forms = ix.entries.filter((e) => e.type === 'formula');
    const xrefs = new Set<string>();
    ix.doc.descendants((n) => { if (n.type.name === 'xref') xrefs.add(n.attrs.target); return true; });
    for (const e of forms) {
      const a = e.attrs;
      const latex: string = a.latex || '';
      if (!latex.trim()) {
        out.push({ sev: 'W', node: e.id, msg: 'Пустая формула - она не попадёт в отчёт',
          fix: { label: 'Удалить', run: (c) => { const at = findPos(c, (n) => n.attrs?.id === e.id); if (at >= 0) c.editor!.chain().deleteRange({ from: at, to: at + 1 }).run(); } } });
        continue;
      }
      const cn = canon(latex);
      if (cn.latex !== latex && cn.notes.length) {
        out.push({ sev: 'W', node: e.id, msg: 'Формула не по СТО (кнопка «Исправить» приведёт её к СТО): ' + cn.notes.map((x) => x.msg).join('; '),
          fix: { label: 'Исправить', run: (c) => setAttrs(c, e.id, { latex: cn.latex }) } });
      }
      // как в полной проверке (FORMULA-NUM-MISSING): выключную формулу нумеруют, без номера - только строку расчёта
      if (!a.numbered && !isCalcLine(latex)) {
        out.push({ sev: 'E', code: 'FORMULA-NUM-MISSING', rule: '4.6.1.6', node: e.id,
          msg: 'Выключная формула без номера - нумеруют все формулы, кроме строк расчёта с подставленными значениями (4.6.1.8)',
          fix: { label: 'Нумеровать', run: (c) => setAttrs(c, e.id, { numbered: true }) } });
      }
      if (a.numbered && isCalcLine(latex)) {
        out.push({ sev: 'W', rule: '4.6.1.8', node: e.id, msg: 'Строка расчёта (подставлены числа) с номером - расчёты не нумеруют',
          fix: { label: 'Убрать номер', run: (c) => setAttrs(c, e.id, { numbered: false }) } });
      }
      const unk = unknownCommands(latex);
      if (unk.length) out.push({ sev: 'W', node: e.id, msg: 'В формуле команды, которые Word выведет текстом или без оформления (зачёркивание, фигурная скобка, цвет, рамка): ' + unk.map((x) => '\\' + x).join(', ') });
      // экспликация
      const where: { sym: string; desc: string }[] = a.where || [];
      const syms = symbols(latex);
      const inFormula = new Set(syms.map(symKey));
      const bad = where.filter((w) => w.sym && !inFormula.has(symKey(w.sym)));
      if (bad.length) out.push({ sev: 'W', rule: '4.6.1.7', node: e.id, msg: 'В экспликации символы, которых нет в формуле: ' + bad.map((w) => w.sym).join(', ') });
      const empty = where.filter((w) => w.sym && !String(w.desc || '').trim());
      if (empty.length) out.push({ sev: 'W', rule: '4.6.1.7', node: e.id, msg: 'Нет расшифровки символа: ' + empty.map((w) => w.sym).join(', '),
        fix: { label: 'Экспликация…', run: (c) => void editWhereById(c, e.id) } });
      for (const w of where) explained.set(symKey(w.sym), true);
      if (!isCalcLine(latex)) {
        const miss = syms.filter((s) => !explained.has(symKey(s)));
        if (miss.length && a.numbered) {
          out.push({ sev: 'I', rule: '4.6.1.7', node: e.id, msg: 'Не пояснены символы формулы' + (info(ix, e.id)) + ': ' + miss.slice(0, 6).join(', ') + (miss.length > 6 ? '…' : ''),
            fix: { label: 'Экспликация «где»…', run: (c) => void editWhereById(c, e.id) } });
        }
        for (const s of syms) explained.set(symKey(s), true);
      }
      if (a.numbered && !xrefs.has(e.id)) {
        out.push({ sev: 'I', rule: '4.6.1.6', node: e.id, msg: 'На формулу' + info(ix, e.id) + ' нет ссылки в тексте («по формуле (N)»)' });
      }
    }
    // абзац «где …» сразу после формулы, набранный вручную: sto_check читает его как экспликацию (4.6.1.7) и
    // требует вида «где x – расшифровка;» - предлагаем перенести в «где…» формулы или перефразировать
    for (let k = 1; k < ix.entries.length; k++) {
      const e = ix.entries[k];
      if (e.type !== 'paragraph' || !/^где[\s ]/i.test(e.text)) continue;
      const prev = [...ix.entries.slice(0, k)].reverse().find((x) => x.depth === e.depth);
      if (!prev || prev.type !== 'formula') continue;
      const rows = parseWhereParagraph(e.node);
      const fixes: LintIssue['fix'] = rows
        ? { label: 'Перенести в экспликацию формулы', run: (c) => moveWhereToFormula(c, e.id, prev.id, rows) }
        : { label: 'Начать абзац со слова «Здесь»', run: (c) => {
          const at = findPos(c, (n) => n.attrs?.id === e.id);
          if (at >= 0) c.editor!.view.dispatch(c.editor!.state.tr.insertText('Здесь', at + 1, at + 4));
        } };
      out.push({ sev: 'E', rule: '4.6.1.7', node: e.id, fix: fixes,
        msg: rows ? 'Экспликация набрана отдельным абзацем - перенесите её в «где…» формулы (запятая, тире и «;» расставятся сами)'
          : 'Абзац после формулы начинается с «где» - так начинается только экспликация «где x – расшифровка;». Перефразируйте или заполните «где…» у формулы' });
    }
    // двоеточие перед формулой (4.6.1.3)
    for (let k = 1; k < ix.entries.length; k++) {
      const e = ix.entries[k];
      if (e.type !== 'formula') continue;
      const prev = [...ix.entries.slice(0, k)].reverse().find((x) => x.depth === e.depth);
      if (!prev || prev.type !== 'paragraph') continue;
      if (/(?:по\s+формул\w*|формул[аеуы]|равн[аоы]?|имеет\s+вид|вычисля\w*|определя\w*|находя\w*|рассчитыва\w*)\s*:\s*$/i.test(prev.text)) {
        out.push({ sev: 'W', rule: '4.6.1.3', node: prev.id, msg: 'Двоеточие перед формулой после «по формуле», «равна», «имеет вид» не ставят',
          fix: { label: 'Убрать двоеточие', run: (c) => {
            const at = findPos(c, (n) => n.attrs?.id === prev.id);
            if (at < 0) return;
            const n = c.editor!.state.doc.nodeAt(at)!;
            const t = n.textContent;
            const m = t.match(/\s*:\s*$/);
            if (!m) return;
            const end = at + 1 + n.content.size;
            c.editor!.view.dispatch(c.editor!.state.tr.delete(end - m[0].length, end));
          } } });
      }
    }
    return out;
  } });
}

/** «где x – описание; y – описание.» (символы - строчные формулы или короткий текст) -> строки экспликации. */
function parseWhereParagraph(p: any): { sym: string; desc: string }[] | null {
  let s = '';
  p.forEach((c: any) => { s += c.type.name === 'mathInline' ? '\u0001' + (c.attrs.latex || '') + '\u0002' : c.isText ? c.text : ' '; });
  s = s.replace(/^где[\s ]+/i, '').replace(/[.\s ]+$/, '');
  const rows: { sym: string; desc: string }[] = [];
  for (const part of s.split(/;\s*/)) {
    const m = part.match(/^\s*(?:\u0001([^\u0002]*)\u0002|([A-Za-zА-Яа-яΑ-ω][\w]{0,3}))\s*[–—-]\s+(.+)$/);
    if (!m || /\u0001/.test(m[3])) return null;
    rows.push({ sym: (m[1] ?? m[2]).trim(), desc: m[3].trim() });
  }
  return rows.length ? rows : null;
}

function moveWhereToFormula(c: Ctx, paraId: string, formulaId: string, rows: { sym: string; desc: string }[]) {
  const ed = c.editor;
  if (!ed) return;
  const fp = findPos(c, (n) => n.attrs?.id === formulaId);
  const pp = findPos(c, (n) => n.attrs?.id === paraId);
  if (fp < 0 || pp < 0) return;
  const f = ed.state.doc.nodeAt(fp)!;
  const para = ed.state.doc.nodeAt(pp)!;
  const tr = ed.state.tr.setNodeMarkup(fp, undefined, { ...f.attrs, where: [...(f.attrs.where || []), ...rows] });
  tr.delete(tr.mapping.map(pp), tr.mapping.map(pp + para.nodeSize));
  ed.view.dispatch(tr);
}

function info(ix: DocIndex, id: string): string {
  const n = ix.numbering.byId.get(id);
  return n?.label ? ' ' + n.label : '';
}

function findParentId(ix: DocIndex, pos: number): string | null {
  const $p = ix.doc.resolve(pos);
  for (let d = $p.depth; d > 0; d--) {
    const id = $p.node(d).attrs?.id;
    if (id) return id;
  }
  return null;
}

async function editWhereById(c: Ctx, id: string) {
  const ed = c.editor;
  if (!ed) return;
  const pos = findPos(c, (n) => n.attrs?.id === id);
  if (pos < 0) return;
  const n = ed.state.doc.nodeAt(pos)!;
  const r = await c.openDialog<any[]>('math.where', { latex: n.attrs.latex, where: n.attrs.where || [], pos, autofill: true });
  if (r) setAttrs(c, id, { where: r });
}
